import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  Paper,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import { Search, Shield, Warning } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { ProtectedBrickItem, Pageable } from '../types';
import { useTokenState } from '../App';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const ProtectedBricks: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [protectedBricks, setProtectedBricks] = useState<ProtectedBrickItem[]>([]);
  const [paginationMeta, setPaginationMeta] = useState<Pageable | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'expired'>('all');

  const { isTokenValid, tokenState } = useTokenState();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset zur ersten Seite bei neuer Suche
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadProtectedBricks = useCallback(async (currentPage: number, currentLimit: number, status?: 'active' | 'expired' | 'all', search?: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getProtectedBricks(
        currentPage,
        currentLimit,
        status === 'all' ? undefined : status,
        search || undefined
      );

      if (data && Array.isArray(data.items)) {
        setProtectedBricks(data.items);
        setPaginationMeta(data.meta);
      } else {
        console.warn('API returned invalid data format:', data);
        setProtectedBricks([]);
        setPaginationMeta(null);
      }
    } catch (err) {
      console.error('Fehler beim Laden der geschützten Steine:', err);
      setError('Fehler beim Laden der Daten. Bitte versuchen Sie es erneut.');
      setProtectedBricks([]);
      setPaginationMeta(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Warte bis Token gültig ist, bevor API-Aufrufe gemacht werden
    if (!isTokenValid) {
      if (tokenState === 'idle' || tokenState === 'checking') {
        setLoading(true);
      } else {
        setLoading(false);
      }
      return;
    }

    loadProtectedBricks(page, limit, statusFilter, debouncedSearch);
  }, [isTokenValid, tokenState, page, limit, statusFilter, debouncedSearch, loadProtectedBricks]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (event: SelectChangeEvent<number>) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<'all' | 'active' | 'expired'>) => {
    setStatusFilter(event.target.value as 'all' | 'active' | 'expired');
    setPage(1);
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getBrickImagePath = (partNumber: string): string => {
    // Remove 'le-' prefix if present
    const cleanPartNumber = partNumber.toLowerCase().startsWith('le-')
      ? partNumber.substring(3)
      : partNumber;
    return `/brick-images/${cleanPartNumber}.png`;
  };

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.src = '/brick-images/placeholder.svg';
  };

  // Loading state
  if (loading && protectedBricks.length === 0) {
    return (
      <Box sx={{ bgcolor: '#f8fafc', minHeight: 'calc(100vh - 64px)', py: 4 }}>
        <Container maxWidth="xl">
          <Card elevation={2}>
            <CardContent sx={{ py: 8, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                Geschmacksmuster werden geladen...
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ bgcolor: '#f8fafc', minHeight: 'calc(100vh - 64px)', py: 4 }}>
        <Container maxWidth="xl">
          <Card elevation={2}>
            <CardContent sx={{ py: 8, textAlign: 'center' }}>
              <Warning sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
              <Typography variant="h6" color="error" gutterBottom>
                Fehler beim Laden
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {error}
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: 'calc(100vh - 64px)', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Shield sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" fontWeight={700}>
              Geschmacksmuster-Datenbank
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Übersicht aller design-geschützten LEGO-Steine
          </Typography>
        </Box>

        {/* Statistics */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Statistik
            </Typography>
            <Typography variant="body1" color="text.secondary">
              <strong>{paginationMeta?.totalItems ?? protectedBricks.length}</strong> Einträge in der Datenbank
              {paginationMeta && paginationMeta.totalPages > 1 && (
                <> &bull; Seite {paginationMeta.page} von {paginationMeta.totalPages}</>
              )}
            </Typography>
          </CardContent>
        </Card>

        {/* Search and Filters */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                sx={{ flexGrow: 1, minWidth: 250 }}
                placeholder="Suche nach LE-Nummer oder GM-Nummer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="medium" sx={{ minWidth: 150 }}>
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  label="Status"
                  onChange={handleStatusFilterChange}
                >
                  <MenuItem value="all">Alle</MenuItem>
                  <MenuItem value="active">Aktiv</MenuItem>
                  <MenuItem value="expired">Abgelaufen</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>

        {/* Table */}
        <Card elevation={2}>
          <CardContent>
            {protectedBricks.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Shield sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Keine Einträge gefunden
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Versuchen Sie eine andere Suche oder Filter
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Bild</strong></TableCell>
                        <TableCell><strong>Teilenummer</strong></TableCell>
                        <TableCell><strong>Beschreibung</strong></TableCell>
                        <TableCell><strong>Geschützt</strong></TableCell>
                        <TableCell><strong>GM-Nummer</strong></TableCell>
                        <TableCell><strong>Schutz-Details</strong></TableCell>
                        <TableCell><strong>Ablaufdatum</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {protectedBricks.map((item) => (
                        <TableRow key={item.partNumber} hover>
                          <TableCell>
                            <Box
                              component="img"
                              src={getBrickImagePath(item.partNumber)}
                              alt={`Brick ${item.partNumber}`}
                              onError={handleImageError}
                              sx={{
                                width: 60,
                                height: 60,
                                objectFit: 'contain',
                                borderRadius: 1,
                                bgcolor: '#f5f5f5',
                                padding: 0.5,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight={600}>{item.partNumber}</Typography>
                          </TableCell>
                          <TableCell>{item.description || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.protected ? 'Ja' : 'Nein'}
                              color={item.protected ? 'error' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{item.gmNumber || '-'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 300 }}>
                              {item.protectionDetails || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatDate(item.expiryDate)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                {paginationMeta && paginationMeta.totalPages > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 3,
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="items-per-page-label">Pro Seite</InputLabel>
                        <Select
                          labelId="items-per-page-label"
                          value={limit}
                          label="Pro Seite"
                          onChange={handleLimitChange}
                        >
                          {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="body2" color="text.secondary">
                        {paginationMeta.pagingCounter ?? ((page - 1) * limit + 1)} -{' '}
                        {Math.min(
                          (paginationMeta.pagingCounter ?? ((page - 1) * limit + 1)) + protectedBricks.length - 1,
                          paginationMeta.totalItems
                        )}{' '}
                        von {paginationMeta.totalItems}
                      </Typography>
                    </Box>

                    <Pagination
                      count={paginationMeta.totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                      siblingCount={1}
                      boundaryCount={1}
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ProtectedBricks;
